/**
 * App shell + routing (008 US1 T009). A tiny hash-router keeps shared-ui free of
 * a router dependency; web and desktop both mount this under AppServicesProvider.
 * Offline state is surfaced globally (008 T021).
 */
import { refreshSettingsCache } from "../stores/sessionWarmup.js";
import { useEffect, useId, useState } from "react";
import { AppServicesProvider, useServices, type AppServices, type UpdateStatus } from "./context.js";
import { Login } from "./Login.js";
import { useSessionStore } from "../stores/session.js";
import { signOutEverywhere } from "./signOut.js";
import { useChainWarning } from "./chainExpiry.js";
import { useReauthPrompt, useReauthStore } from "./reauth.js";
import { CustomerTemplates } from "../templates/CustomerTemplates.js";
import { GeneratePanel } from "../generate/GeneratePanel.js";
import { MockupsOnlyPage } from "../mockupsOnly/MockupsOnlyPage.js";
import { requestTemplateOpen } from "../stores/templateFocus.js";
import { confirmDiscardWizard, openWizardOnMockup } from "../stores/wizard.js";
import { BrandsPanel } from "../branding/BrandsPanel.js";
import { SettingsPanel } from "../settings/SettingsPanel.js";
import { ExportWorkspace, type ExportTab } from "./ExportWorkspace.js";
import { Editor } from "./Editor.js";
import { ErrorBoundary } from "./ErrorBoundary.js";
import { AdminSection } from "../admin/AdminSection.js";
import { DashboardSection } from "../observability/ObservabilitySection.js";
import { canAccessAdmin } from "../admin/gating.js";
import { Icon, type IconName } from "../ui/Icon.js";
import { SpeedTestPage } from "../speedtest/SpeedTestPage.js";
import { ToastHost } from "../ui/Toast.js";
import { useAppearance, DEFAULT_APPEARANCE } from "../stores/appearance.js";
import { downloadSegmentationModel, isModelCached, formatSpeed, formatEta, setModelsBase, type DownloadProgress } from "../stores/modelStore.js";
import { setHostingEnabled } from "../templates/bakedSyncCloud.js";
import { applyTheme, getTheme } from "../stores/theme.js";

// Apply the saved theme before first paint, so an explicit Light/Dark choice never flashes
// the OS theme first. "system" removes the attribute and `prefers-color-scheme` decides.
applyTheme(getTheme());

// Spec 017 US3 nav: customers get `browse` (template search/sync, merges old Sync) +
// `generate` (studio plan only); the admin-only `editor` holds template authoring +
// options + brand approval + settings; the old `studio` (batch/queue/export) → `export`.
type Route = "browse" | "generate" | "brands" | "export" | "mockups-only" | "settings" | "editor" | "admin" | "dashboard";
const ROUTES: Route[] = ["browse", "generate", "brands", "export", "mockups-only", "settings", "editor", "admin", "dashboard"];

function useHashRoute(): [Route, (r: Route) => void] {
  const [route, setRoute] = useState<Route>(() => readHash());
  useEffect(() => {
    const onHash = () => setRoute(readHash());
    if (typeof window !== "undefined") window.addEventListener("hashchange", onHash);
    return () => {
      if (typeof window !== "undefined") window.removeEventListener("hashchange", onHash);
    };
  }, []);
  const nav = (r: Route) => {
    if (typeof window !== "undefined") window.location.hash = r;
    setRoute(r);
  };
  return [route, nav];
}

/**
 * The routes opened since sign-in. Each stays mounted — hidden while another is on screen — so
 * leaving a page and coming back finds it as it was left, a running generation included.
 * Signing out forgets them, so the next account's pages start clean.
 */
function useVisitedRoutes(route: Route, signedIn: boolean): ReadonlySet<Route> {
  const [visited, setVisited] = useState<ReadonlySet<Route>>(() => new Set());
  if (!signedIn) {
    if (visited.size > 0) setVisited(new Set());
  } else if (!visited.has(route)) {
    setVisited(new Set(visited).add(route));
  }
  return visited;
}

function readHash(): Route {
  const h = (typeof window !== "undefined" ? window.location.hash : "").replace("#", "");
  // Backward-compat redirects: old template-authoring/sync routes → customer `browse`;
  // old batch/queue/export/studio → `export`.
  if (h === "wizard" || h === "templates" || h === "sync") return "browse";
  if (h === "batch" || h === "queue" || h === "studio") return "export";
  if (h === "observability") return "dashboard";
  return ROUTES.includes(h as Route) ? (h as Route) : "browse";
}

const ROUTE_META: Record<Route, { label: string; icon: IconName }> = {
  browse: { label: "Templates", icon: "templates" },
  generate: { label: "Generate", icon: "generate" },
  brands: { label: "Brands", icon: "brands" },
  export: { label: "Mock-ups", icon: "mockups" },
  "mockups-only": { label: "Only mock-ups", icon: "image" },
  settings: { label: "Settings", icon: "settings" },
  editor: { label: "Editor", icon: "editor" },
  admin: { label: "Admin", icon: "admin" },
  dashboard: { label: "Dashboard", icon: "dashboard" },
};

/** The Update button's words while it works: the model, then the update's own progress. */
function updateLabel(model: DownloadProgress | null, update: UpdateStatus | null): string {
  if (model) return "Downloading AI model…";
  if (update?.phase === "downloading") return `Downloading update ${Math.round(update.pct * 100)}%`;
  if (update?.phase === "installing") return "Installing — the app will restart";
  return "Updating…";
}

function Shell() {
  const services = useServices();
  const { io } = services;
  const [checking, setChecking] = useState(false);
  /** Binds the web Version row's visible reason to the row itself (review-1 F-5). */
  const versionWhyId = useId();
  /** Live AI-model download progress (drives the Update progress bar); null when idle. */
  const [modelProgress, setModelProgress] = useState<DownloadProgress | null>(null);
  const [updateStatus, setUpdateStatus] = useState<UpdateStatus | null>(null);
  const [speedTestOpen, setSpeedTestOpen] = useState(false);
  const session = useSessionStore((s) => s.session);
  // WS-Q / IMP-13: how long this session CHAIN has left. `none` until the cloud actually says.
  const chain = useChainWarning();
  const reauth = useReauthPrompt();
  const role = useSessionStore((s) => s.role());
  const isSuper = useSessionStore((s) => s.isSuperAdmin());
  const [route, nav] = useHashRoute();
  const visited = useVisitedRoutes(route, !!session && !reauth.pending);
  const [online, setOnline] = useState(io.isOnline());
  const [sidebarOpen, setSidebarOpen] = useState(false);
  /** WS-B B-9: the Export sub-tab an in-app handoff asked for (see `goExport` below). */
  const [exportTabRequest, setExportTabRequest] = useState<ExportTab | undefined>(undefined);
  const appearance = useAppearance();
  // The customer's catalog edition (brand|studio) gates the customer nav (spec 017 US3):
  // only `studio` customers get the Generate tab. Sourced from the capabilities endpoint
  // (the session doesn't carry it). Resilient — stays undefined (Generate hidden) on error.
  const [edition, setEdition] = useState<string | undefined>(undefined);

  useEffect(() => io.onConnectivityChange(setOnline), [io]);
  const signedIn = !!session;
  useEffect(() => {
    if (!signedIn) return;
    const refresh = () => { if (document.visibilityState === "visible") void refreshSettingsCache(services.client); };
    refresh();
    document.addEventListener("visibilitychange", refresh);
    return () => document.removeEventListener("visibilitychange", refresh);
  }, [signedIn, services.client]);
  // Gated routes (#editor / #admin / #dashboard) used to render an EMPTY `main` for anyone
  // without the role: working nav, blank page, and a URL claiming otherwise. Send them to
  // the browse route so the address and the screen always agree.
  useEffect(() => {
    if (!session) return;
    const gated = route === "editor" || route === "admin" || route === "dashboard";
    if (!gated) return;
    const allowed = route === "dashboard" ? isSuper : canAccessAdmin(role);
    if (!allowed) nav("browse");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `nav` is recreated every render.
  }, [route, role, isSuper, session]);
  // A4: `#generate` is a studio customer's page. A customer waits for the edition, and one on
  // another edition — or whose edition could not be read — is sent to Templates. Only mock-ups
  // holds Generate's images, so it follows the same gate.
  const [editionUnreadable, setEditionUnreadable] = useState(false);
  const generateAllowed = role !== "customer" || edition === "studio";
  useEffect(() => {
    const gated = route === "generate" || route === "mockups-only";
    if (gated && role === "customer" && (editionUnreadable || (edition && edition !== "studio"))) nav("browse");
  // eslint-disable-next-line react-hooks/exhaustive-deps -- `nav` is recreated every render.
  }, [route, role, edition, editionUnreadable]);
  useEffect(() => { services.logger?.setRoute(route); }, [route, services.logger]);
  // WS-B B-9 / review-1 m-4: the Export sub-tab request belongs to ONE navigation. `go()`
  // clears it, but browser history does not go through `go()` — so Back to #browse and
  // Forward to #export re-applied a request the operator made once and landed them on Batch
  // again. Leaving #export by ANY route (a nav click, Back, Forward, a typed hash) drops it,
  // which is the same rule stated where it cannot be bypassed.
  useEffect(() => {
    if (route !== "export") setExportTabRequest(undefined);
  }, [route]);
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const caps = await services.client.customizationCapabilities?.();
        if (!cancelled && caps) setEdition(caps.edition);
        // D-18: the cloud owns where the segmentation models live.
        if (!cancelled && caps) setModelsBase(caps.models_base ?? null);
        // WS-P: warm the hosting capability ONCE here so the bake path never has to ask. No
        // `??`: an older cloud omits the field, and the editor must read that as "nobody said"
        // (still attempt, the 409 is the backstop) rather than as "hosting is off".
        if (!cancelled && caps) setHostingEnabled(caps.hosting_enabled);
      } catch {
        // Undefined → Generate stays hidden, and a customer on `#generate` leaves it.
        if (!cancelled) setEditionUnreadable(true);
      }
    })();
    return () => { cancelled = true; };
  }, [services.client, session]);

  // A re-authenticate prompt outranks the shell. Set by an involuntary expiry (Q-1, where the
  // session is already gone) AND by the expiry banner's own button, where the session is still
  // perfectly good — asking someone to sign in again is not signing them out, and nothing in
  // the workspace is touched either way.
  if (!session || reauth.pending) return <Login />;

  // On mobile the sidebar is a slide-in drawer; navigating closes it. A7 — navigating never
  // discards in-progress work: the wizard and Generate stores keep it until Publish or Cancel.
  /**
   * WS-B B-9 — `opts.exportTab` is which Export sub-tab an in-app handoff asked for.
   *
   * Deliberately route STATE, not a route: `#export` stays one route with one URL, so a
   * bookmark and a reload land on Select, and a sidebar click returns to the sub-tab the
   * operator left. Every plain navigation CLEARS the request (it is set only by `goExport`),
   * and the effect above clears it for the navigations that never reach this function at
   * all (Back / Forward), so a request applies to exactly one arrival.
   */
  const go = (r: Route, opts?: { exportTab?: ExportTab }) => {
    setExportTabRequest(opts?.exportTab);
    nav(r);
    setSidebarOpen(false);
  };

  const exportTab = route === "export" ? exportTabRequest : undefined;
  const goExport = (tab?: ExportTab) => go("export", { exportTab: tab });

  // Admin is for real operators only — gated by role. The server re-checks every
  // admin request, so this is purely which nav/links to show.
  const showAdmin = canAccessAdmin(role);

  // Nav by role + edition (spec 017 US3). A super-admin gets the FULL customer experience
  // (a "Customer preview" group) so they can test it out exactly as a customer would —
  // Generate is shown regardless of edition for testing — PLUS their admin tooling. A
  // tenant-admin gets the admin tools; a customer gets their own tabs (Generate only on
  // the studio plan).
  const navGroups: { label: string; items: Route[] }[] = [];
  if (isSuper) {
    navGroups.push({ label: "Customer preview", items: ["generate", "browse", "brands", "export", "mockups-only", "settings"] });
    navGroups.push({ label: "Admin", items: ["editor", "admin", "dashboard"] });
  } else if (showAdmin) {
    navGroups.push({ label: "Workspace", items: ["editor", "export", "mockups-only", "admin"] });
  } else {
    const items: Route[] = [];
    if (edition === "studio") items.push("generate");
    items.push("browse", "brands", "export");
    if (edition === "studio") items.push("mockups-only");
    items.push("settings");
    navGroups.push({ label: "Workspace", items });
  }
  const initial = (session.email ?? session.customerId ?? "?").charAt(0).toUpperCase();

  return (
    <div className={`shell${sidebarOpen ? " shell--sidebar-open" : ""}`} data-testid="shell">
      {/* AUD-5 M-4: a <button> with no children and no accessible name. It is hidden and
          untabbable while the sidebar is closed, but a name costs nothing and the element is
          announced the moment the drawer opens. */}
      <button
        type="button"
        className="shell__scrim"
        data-testid="sidebar-scrim"
        aria-label="Close navigation"
        aria-hidden={!sidebarOpen}
        tabIndex={-1}
        onClick={() => setSidebarOpen(false)}
      />
      <aside className="shell__sidebar">
        <div className="shell__brand">
          {appearance.logo_url ? (
            <img className="brand-logo" src={appearance.logo_url} alt="" aria-hidden="true" />
          ) : appearance.short_mark === DEFAULT_APPEARANCE.short_mark ? (
            <img className="brand-logo brand-logo--mark" src="/brand-mark.svg" alt="" aria-hidden="true" />
          ) : (
            <span className="brand-mark" aria-hidden="true">{appearance.short_mark}</span>
          )}
          <span className="brand-name">
            <strong>{appearance.app_name} <CloudIndicator online={online} /></strong>
          </span>
        </div>
        <nav className="shell__nav" aria-label="Primary">
          {navGroups.map((group) => (
            <div className="shell__nav-group" key={group.label}>
              <div className="shell__nav-label">{group.label}</div>
              {group.items.map((r) => (
                <button
                  key={r}
                  type="button"
                  className="nav-item"
                  onClick={() => go(r)}
                  // Navigation semantics: aria-current marks "you are here" for screen
                  // readers; aria-pressed wrongly announced these as toggle buttons.
                  aria-current={route === r ? "page" : undefined}
                >
                  <span className="nav-item__icon"><Icon name={ROUTE_META[r].icon} /></span>
                  <span>{ROUTE_META[r].label}</span>
                </button>
              ))}
            </div>
          ))}
        </nav>
        <div className="shell__sidebar-foot">
          <button
            type="button"
            className="btn btn--sm btn--block shell__update-btn"
            data-testid="open-speed-test"
            onClick={() => setSpeedTestOpen(true)}
          >
            <span className="shell__update-ico"><Icon name="bolt" size={16} /></span>
            <span>Speed test</span>
          </button>
          {services.checkForUpdates ? (
            <>
            <button
              type="button"
              className="btn btn--sm btn--block shell__update-btn"
              data-testid="check-updates"
              disabled={checking}
              onClick={async () => {
                setChecking(true);
                try {
                  // Approved flow: download the on-device AI model HERE (explicit, with a
                  // visible progress bar) when it isn't cached — never silently on first
                  // editor use, which froze the app. A failed model download must not
                  // block the app update that follows (2026-10-22).
                  try {
                    if (!(await isModelCached())) {
                      await downloadSegmentationModel((p) => setModelProgress(p));
                    }
                  } catch (e) {
                    services.logger?.log("warn", "Update · the AI model download failed; checking for updates anyway", { error: e });
                  }
                  setModelProgress(null);
                  await services.checkForUpdates!(setUpdateStatus);
                } finally {
                  setChecking(false);
                  setModelProgress(null);
                  setUpdateStatus(null);
                }
              }}
            >
              {checking ? (
                <span className="shell__update-spinner" aria-hidden="true" />
              ) : (
                <span className="shell__update-ico"><Icon name="arrow-up" size={16} /></span>
              )}
              <span>{checking ? updateLabel(modelProgress, updateStatus) : "Update"}</span>
              <span className="shell__update-version" data-testid="app-version">v{services.appVersion}</span>
            </button>
            {modelProgress && (
              <div data-testid="model-download-progress" className="shell__model-progress">
                <div className="seg-model__bar">
                  {/* The WIDTH is data — a live download percentage — so it stays inline.
                      Everything else about the bar is in CSS; the 11px caption that used to
                      sit beside it was below the 12px floor (D-34). */}
                  <div className="seg-model__fill" style={{ width: `${Math.round(modelProgress.pct * 100)}%` }} />
                </div>
                <span>
                  AI model {Math.round(modelProgress.pct * 100)}%
                  {modelProgress.bytesPerSec > 0 ? ` · ${formatSpeed(modelProgress.bytesPerSec)}` : ""}
                  {modelProgress.etaSec > 0 ? ` · ~${formatEta(modelProgress.etaSec)} left` : ""}
                </span>
              </div>
            )}
            </>
          ) : (
            /* Cloud/web has no self-update action — still surface the running version.
               R10: this was a `<div>` wearing `.btn btn--sm btn--block`. A div is not a
               control: Tab skips it, a screen reader announces nothing, and the row said
               "Version" without ever saying WHY it could not be pressed.

               review-1 F-5: the first fix made it a `disabled` button, which Tab skips for the
               same reason the `<div>` did, and put the reason in a `title`, which only a
               pointer can reveal. `aria-disabled` instead of `disabled` — focusable, announced
               as dimmed, and inert on activation — with the reason as a VISIBLE caption under
               the row that `aria-describedby` binds to the control. The row is not an
               invitation and still does not look like one: `.btn[aria-disabled="true"]` wears
               exactly what `.btn:disabled` wears. */
            <>
            <button
              type="button"
              className="btn btn--sm btn--block shell__update-btn"
              data-testid="app-version-row"
              aria-disabled="true"
              aria-describedby={versionWhyId}
              onClick={(e) => e.preventDefault()}
            >
              <span className="shell__update-ico"><Icon name="arrow-up" size={16} /></span>
              <span>Version</span>
              <span className="shell__update-version" data-testid="app-version">v{services.appVersion}</span>
            </button>
            <p className="shell__update-why" id={versionWhyId} data-testid="app-version-why">
              The web app updates itself — reload the page to pick up a new build.
            </p>
            </>
          )}
          <div className="shell__user">
            <span className="shell__avatar" aria-hidden="true">{initial}</span>
            <span className="shell__user-meta">
              <strong>{session.email ?? session.customerId}</strong>
              <span>{role ? role.replace("_", " ") : "member"}</span>
            </span>
            <button
              type="button"
              className="btn btn--sm btn--icon shell__signout"
              onClick={() => {
                // REVOKE FIRST, then purge. `client.logout()` had no caller at all, so "Sign
                // out" only ever cleared THIS device and the refresh token stayed live on the
                // server for the rest of its chain. Ordering is load-bearing: step 1 of the
                // purge clears the very token the revoke has to present.
                //
                // `.finally` and not `.then`: `logout()` already swallows its own request
                // failure, and an offline sign-out must still sign the operator out locally.
                // The `.catch` in front covers everything `logout()` does NOT swallow, so a
                // failed revoke can never leave the operator signed in with an unhandled
                // rejection instead of a purge.
                void services.client.logout().catch(() => {}).finally(() =>
                  // A DELIBERATE sign-out, possibly on a shared machine: session + tokens,
                  // buffered telemetry/logs, the localStorage cache, AND every device store
                  // holding this account's content. `reason: "user"` is what asks for that
                  // second tier — an expiry (bootstrap.ts) deliberately keeps it.
                  signOutEverywhere({
                    io: services.io,
                    engine: services.engine,
                    log: (level, message, ctx) => services.logger?.log(level, message, ctx),
                    // H-2: the COMPOSED purge (telemetry + client log). The logger alone left
                    // the telemetry buffer for the next account's token to flush.
                    purgeBuffers: () => services.purgeAccountBuffers(),
                  }, { reason: "user" }),
                );
              }}
              aria-label="Sign out"
              title="Sign out"
            >
              <Icon name="power" size={16} />
            </button>
          </div>
        </div>
      </aside>

      <div className="shell__content">
        <header className="shell__top">
          <button
            type="button"
            className="shell__menu-btn"
            data-testid="sidebar-toggle"
            aria-label="Toggle menu"
            aria-expanded={sidebarOpen}
            onClick={() => setSidebarOpen((v) => !v)}
          >
            <Icon name="menu" size={20} />
          </button>
        </header>

        {/* IMP-13: the cliff, said out loud. One warn tone, whatever the tier — WS-B's status
            vocabulary has exactly one, and this wave does not invent a third. Absent entirely
            when the cloud never sent an expiry: silence beats an invented countdown. */}
        {chain.level !== "none" && (
          <p className="banner banner--warn" role="status" data-testid="chain-expiry-banner">
            {chain.hoursLeft > 0
              ? `Your sign-in expires in about ${chain.hoursLeft} ${chain.hoursLeft === 1 ? "hour" : "hours"}. `
              : "Your sign-in is about to expire. "}
            Sign in again to avoid being interrupted mid-run.{" "}
            <button
              type="button"
              className="btn btn--sm btn--ghost"
              data-testid="chain-expiry-reauth"
              onClick={() => useReauthStore.getState().open(session.email)}
            >
              Sign in again
            </button>
          </p>
        )}

        {/* One boundary per page: a crash is contained to the panel (the nav keeps working),
            and a crashed page retries with a clean mount when it is shown again. Inactive
            pages stay mounted but hidden and inert — out of the tab order and the a11y tree. */}
        <main className="shell__main">
          {ROUTES.filter((r) => visited.has(r)).map((r) => (
            <div
              key={r}
              className={`shell__page${r === route ? " shell__page--active" : ""}`}
              data-route={r}
              hidden={r !== route}
              inert={r !== route}
            >
              <ErrorBoundary name={ROUTE_META[r].label} shown={r === route}>
                {r === "browse" && <CustomerTemplates onGoExport={goExport} />}
                {r === "generate" && generateAllowed && (
                  <GeneratePanel active={r === route} onTemplateDone={() => go("browse")}
                    onOpenTemplate={(id) => { requestTemplateOpen(id); go("browse"); }} />
                )}
                {r === "brands" && <BrandsPanel />}
                {r === "settings" && <SettingsPanel />}
                {r === "export" && <ExportWorkspace onGoTemplates={() => nav("browse")} initialTab={exportTab} />}
                {r === "mockups-only" && generateAllowed && (
                  <MockupsOnlyPage active={r === route} onMakeTemplate={(image, details) => {
                    if (!confirmDiscardWizard()) return;
                    openWizardOnMockup(image, details);
                    go("editor");
                  }} />
                )}
                {r === "editor" && showAdmin && <Editor />}
                {r === "admin" && showAdmin && <AdminSection />}
                {r === "dashboard" && isSuper && <DashboardSection />}
              </ErrorBoundary>
            </div>
          ))}
        </main>
      </div>

      {speedTestOpen && <SpeedTestPage onClose={() => setSpeedTestOpen(false)} />}
      {/* THE toast mount site, once, in the shell. A confirmation belongs to the window,
          not to the panel that raised it — the panel may have re-rendered away by then. */}
      <ToastHost />
    </div>
  );
}

/** Live cloud-connection dot next to the app name: green when connected (pulses
 *  while a request is in flight), RED when the browser is offline OR the cloud is
 *  unreachable (last request errored / 5xx). Listens to the client's
 *  `genz:cloud-activity` + `genz:cloud-health` events. */
function CloudIndicator({ online }: { online: boolean }) {
  const [busy, setBusy] = useState(false);
  const [healthy, setHealthy] = useState(true);
  useEffect(() => {
    if (typeof window === "undefined") return;
    let clear: ReturnType<typeof setTimeout> | undefined;
    const onActivity = (e: Event) => {
      const active = (e as CustomEvent<{ active: boolean }>).detail?.active;
      if (active) {
        setBusy(true);
      } else {
        // brief lingering blink so quick requests are visible
        clear = setTimeout(() => setBusy(false), 250);
      }
    };
    const onHealth = (e: Event) => {
      setHealthy((e as CustomEvent<{ ok: boolean }>).detail?.ok ?? true);
    };
    window.addEventListener("genz:cloud-activity", onActivity);
    window.addEventListener("genz:cloud-health", onHealth);
    return () => {
      window.removeEventListener("genz:cloud-activity", onActivity);
      window.removeEventListener("genz:cloud-health", onHealth);
      if (clear) clearTimeout(clear);
    };
  }, []);
  const connected = online && healthy;
  const state = !connected ? "offline" : busy ? "busy" : "online";
  const title = !online
    ? "Offline — no network"
    : !healthy
      ? "Cloud unreachable"
      : busy
        ? "Syncing with cloud…"
        : "Connected to cloud";
  return (
    <span
      className={`cloud-dot cloud-dot--${state}`}
      data-testid="cloud-indicator"
      data-state={state}
      role="status"
      aria-label={title}
      title={title}
    />
  );
}

export function App({ services }: { services: AppServices }) {
  return (
    <AppServicesProvider services={services}>
      {/* Outermost guard: a crash in the SHELL itself (nav, header, connectivity) used to
          blank the whole window. This one is never re-keyed — it is the last line. */}
      <ErrorBoundary name="Image Maker">
        <Shell />
      </ErrorBoundary>
    </AppServicesProvider>
  );
}
